import { useState, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useClientId } from "@/shared/hooks/useClientId";
import { useClientSelection } from "@/features/projects/contexts/ClientSelectionContext";
import { useProjects } from "@/features/projects/hooks/useProjects";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/shared/ui/dropdown-menu";
import { cn } from "@/shared/lib/utils";
import { Badge } from "@/shared/ui/badge";
import { firebaseAuth } from "@/shared/integrations/firebase/client";
import { useExportsEnabled } from "@/shared/hooks/useExportsEnabled";

/** Firebase UIDs that can see the Site Surveys link */
import { isAdminUser } from "@/shared/lib/adminUids";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from "@/shared/ui/sidebar";
import { SidebarFooter as CustomSidebarFooter } from "@/features/projects/components/owner/SidebarFooter";
import {
  Building2,
  Package,
  Bot,
  ClipboardCheck,
  GraduationCap,
  Download,
  Search,
  ChevronsUpDown,
  Code2,
  PanelLeft,
  ExternalLink,
} from "lucide-react";

export default function ClientSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { open, toggleSidebar, isMobile } = useSidebar();
  const { rawProjects } = useProjects();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { clientId } = useClientId();
  const { setSelectedClientId } = useClientSelection();

  // Find current project
  const currentProject = rawProjects.find((p) => p.id === clientId);

  // Persist the chosen client, then return to the dashboard so we don't sit on
  // a property/asset that belongs to the previously-selected client.
  const handleClientChange = (newClientId: string) => {
    setSelectedClientId(newClientId);
    navigate("/dashboard");
  };

  const isActive = (path: string) => {
    // Exact match for dashboard
    if (path === "/dashboard") {
      return location.pathname === path;
    }

    // Properties: active when on /properties or /property/:id
    if (path === "/properties") {
      return location.pathname === path || location.pathname.startsWith("/properties/") || location.pathname.startsWith("/property/");
    }

    // Assets: active when on /assets or /asset/:id
    if (path === "/assets") {
      return location.pathname === path || location.pathname.startsWith("/asset/");
    }

    // Exact match for others
    return location.pathname === path;
  };

  const isSurveyAdmin = isAdminUser(firebaseAuth?.currentUser?.uid);
  const { enabled: exportsEnabled } = useExportsEnabled(clientId);

  // Classic View links back to the legacy app — only on the production host.
  const showClassicView =
    typeof window !== "undefined" &&
    (window.location.hostname === "roboticimaging.ai" ||
      window.location.hostname.endsWith(".roboticimaging.ai"));

  // Client-specific menu - conditionally includes Retail Dashboard for allowed clients only
  const menuItems = useMemo(() => {
    type MenuItem = {
      title: string;
      icon: typeof Bot;
      href: string;
      badge?: string;
    };

    const items: MenuItem[] = [];

    items.push(
      {
        title: "AI Agent",
        icon: Bot,
        href: "/dashboard",
      },
      {
        title: "Properties",
        icon: Search,
        href: "/properties",
      },
    );

    items.push({
      title: "Assets",
      icon: Package,
      href: "/assets",
    });

    items.push({
      title: "Developer",
      icon: Code2,
      href: "/developer",
    });



    if (exportsEnabled) {
      items.push({
        title: "Import & Export",
        icon: Download,
        href: "/exports",
      });
    }

    // Site Surveys — only visible to admin UIDs
    if (isSurveyAdmin) {
      items.push({
        title: "Site Surveys",
        icon: ClipboardCheck,
        href: "/site-surveys",
      });
    }

    // Learning — available to all clients.
    items.push({
      title: "Learning",
      icon: GraduationCap,
      href: "/learning",
    });

    return items;
  }, [clientId, isSurveyAdmin, exportsEnabled]);

  const isSidebarOpen = open;

  return (
    <div className="h-full">
      <Sidebar className="border-r border-sidebar-border bg-sidebar" collapsible="icon">
        {/* Header with branding / Client Picker */}
        <SidebarHeader 
          className="border-b border-sidebar-border bg-gradient-to-r from-primary/10 to-primary/5"
          data-dropdown-open={isDropdownOpen}
        >
          <div className={cn("flex", isSidebarOpen ? "justify-end" : "justify-center")}>
            <button
              type="button"
              onClick={toggleSidebar}
              aria-label={isSidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
              title={isSidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
              className="rounded-md p-1.5 text-sidebar-foreground/70 transition-colors hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
            >
              <PanelLeft className="h-4 w-4" />
            </button>
          </div>
          {rawProjects.length > 1 ? (
            <SidebarMenu>
              <SidebarMenuItem>
                <DropdownMenu onOpenChange={setIsDropdownOpen}>
                  <DropdownMenuTrigger asChild>
                    <SidebarMenuButton
                      size="lg"
                      className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground border border-transparent hover:border-sidebar-border"
                    >
                      <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <Building2 className="size-4" />
                      </div>
                      <div className="grid flex-1 text-left text-sm leading-tight">
                        <span className="truncate font-bold text-sidebar-foreground">{currentProject?.name || "Select Client"}</span>
                        <span className="truncate text-xs text-sidebar-foreground/70">Client</span>
                      </div>
                      <ChevronsUpDown className="ml-auto size-4" />
                    </SidebarMenuButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                    align="start"
                    side={isMobile ? "bottom" : "right"}
                    sideOffset={4}
                  >
                    {rawProjects.map((project) => (
                      <DropdownMenuItem
                        key={project.id}
                        onClick={() => handleClientChange(project.id)}
                        className="cursor-pointer py-2 px-3"
                      >
                        <Building2 className="mr-2 h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{project.name}</span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </SidebarMenuItem>
            </SidebarMenu>
          ) : (
            <div
              className={cn(
                "flex items-center py-4 transition-all duration-300",
                isSidebarOpen ? "space-x-3 px-4" : "justify-center px-2",
              )}
            >
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <Building2 className="h-5 w-5 text-primary" />
              </div>
              <div
                className={cn(
                  "overflow-hidden transition-all duration-300",
                  isSidebarOpen ? "w-auto opacity-100" : "w-0 opacity-0",
                )}
              >
                <h2 className="text-lg font-bold text-sidebar-foreground whitespace-nowrap">
                  {currentProject?.name || "PropertyFlow"}
                </h2>
                <p className="text-xs text-sidebar-foreground/70 whitespace-nowrap">Asset Management</p>
              </div>
            </div>
          )}
        </SidebarHeader>

        {/* Navigation Menu */}
        <SidebarContent className={cn("py-4 transition-all duration-300", isSidebarOpen ? "px-3" : "px-0")}>
          <SidebarGroup>
            <SidebarMenu className={cn("space-y-1", !isSidebarOpen && "flex flex-col items-center")}>
              {menuItems.map((item) => (
                <SidebarMenuItem key={item.href} className={cn("w-full", !isSidebarOpen && "flex justify-center")}>
                  <SidebarMenuButton
                    asChild
                    isActive={isActive(item.href)}
                    tooltip={!isSidebarOpen ? item.title : undefined}
                    className={cn(
                      "transition-all duration-200 font-medium",
                      "text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50",
                      isActive(item.href) && "text-primary bg-primary/10",
                      !isSidebarOpen && "!w-10 !h-10 !p-0 rounded-lg justify-center",
                    )}
                  >
                    <Link to={item.href} className={cn("flex items-center gap-3", !isSidebarOpen && "justify-center")}>
                      <item.icon className={cn("h-5 w-5 flex-shrink-0", isActive(item.href) && "text-primary")} />
                      {isSidebarOpen && (
                        <>
                          <span className="whitespace-nowrap">{item.title}</span>
                          {item.badge && (
                            <Badge variant="secondary" className="ml-auto text-xs h-5">
                              {item.badge}
                            </Badge>
                          )}
                        </>
                      )}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
              {showClassicView && (
                <SidebarMenuItem className={cn("w-full", !isSidebarOpen && "flex justify-center")}>
                  <SidebarMenuButton
                    asChild
                    tooltip={!isSidebarOpen ? "Classic View" : undefined}
                    className={cn(
                      "transition-all duration-200 font-medium",
                      "text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/50",
                      !isSidebarOpen && "!w-10 !h-10 !p-0 rounded-lg justify-center",
                    )}
                  >
                    <a
                      href="https://roboticimaging.ai/"
                      className={cn("flex items-center gap-3", !isSidebarOpen && "justify-center")}
                    >
                      <ExternalLink className="h-5 w-5 flex-shrink-0" />
                      {isSidebarOpen && <span className="whitespace-nowrap">Classic View</span>}
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </SidebarMenu>
          </SidebarGroup>
        </SidebarContent>

        {/* Footer with user menu and sign out */}
        <SidebarFooter>
          <CustomSidebarFooter />
        </SidebarFooter>
      </Sidebar>
    </div>
  );
}
